import React, { useState, useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../ui/tabs';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import { AlertCircle, Truck, Trash2, Loader2, CalendarDays } from 'lucide-react';
import { STATUS_CODES, extractFahrzeugNummer } from './constants';
import { dienstApi } from '../../lib/api';
import { cn } from '../../lib/utils';

// Reihenfolge und sichtbare Status-Buttons (identisch mit Status-Tab im
// Einzel-Dialog, damit Nutzer sich nicht umgewöhnen müssen).
const STATUS_BUTTONS = ['K', 'SU', 'F', 'WA', 'RW', 'T', 'ZBV', '--'];

/**
 * BulkAssignmentDialog — bearbeitet mehrere selektierte Zellen eines Mitarbeiters
 * gleichzeitig. Tabs: Funktion | Status | Leeren.
 *
 * Erwartet:
 *  - selectedCells: Array von { mitarbeiter_id, datum, dienst_art }
 *  - mitarbeiter: Der (einzige) Mitarbeiter (Multi-Mitarbeiter-Auswahl ist NICHT erlaubt)
 *  - fahrzeuge: Fahrzeug-Liste zum Filtern der Funktionen
 *  - abteilungId: aktuell angezeigte Wachabteilung — wird als Kontext mitgeschickt
 *  - onDone: Callback nach erfolgreichem/teilweisem Bulk-Save (löscht Auswahl + reloadet Matrix)
 */
export function BulkAssignmentDialog({
  open, onOpenChange, selectedCells = [], mitarbeiter, fahrzeuge = [], abteilungId, onDone,
}) {
  const [mode, setMode] = useState('funktion');
  const [selectedFahrzeugId, setSelectedFahrzeugId] = useState('');
  const [selectedFunktionKuerzel, setSelectedFunktionKuerzel] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const cellCount = selectedCells.length;

  const hauptFahrzeuge = useMemo(
    () => fahrzeuge.filter((f) => f.kann_hauptfahrzeug_sein !== false),
    [fahrzeuge]
  );

  const funktionenFuerFahrzeug = useMemo(() => {
    const fz = hauptFahrzeuge.find((f) => f.id === selectedFahrzeugId);
    return fz?.funktionen || [];
  }, [selectedFahrzeugId, hauptFahrzeuge]);

  const resetForm = () => {
    setSelectedFahrzeugId('');
    setSelectedFunktionKuerzel('');
    setMode('funktion');
  };

  // Sequenzielle Verarbeitung — vermeidet Race Conditions am Backend (mehrfache
  // Konflikt-Prüfungen auf gleicher Position). Bei > 25 Zellen könnte parallel
  // sinnvoll sein, aber der typische Use-Case (2 Wochen krank) ist < 30.
  const runBulk = async (worker) => {
    setIsSaving(true);
    let ok = 0;
    const fehler = [];
    for (const cell of selectedCells) {
      try {
        await worker(cell);
        ok += 1;
      } catch (e) {
        const msg = e?.response?.data?.detail || e?.message || 'Fehler';
        fehler.push({ cell, msg: typeof msg === 'string' ? msg : 'Fehler' });
      }
    }
    setIsSaving(false);
    if (ok > 0 && fehler.length === 0) {
      toast.success(`${ok} von ${cellCount} Zellen aktualisiert`);
    } else if (ok > 0) {
      toast.warning(`${ok} aktualisiert, ${fehler.length} Fehler`, {
        description: fehler.slice(0, 3).map((f) => `${f.cell.datum} ${f.cell.dienst_art}: ${f.msg}`).join('\n'),
      });
    } else {
      toast.error(`Alle ${cellCount} Zellen fehlgeschlagen`, {
        description: fehler.slice(0, 3).map((f) => `${f.cell.datum} ${f.cell.dienst_art}: ${f.msg}`).join('\n'),
      });
    }
    resetForm();
    onOpenChange(false);
    if (onDone) onDone();
  };

  const applyStatus = (statusCode) => {
    runBulk((cell) =>
      dienstApi.setStatus({
        mitarbeiter_id: cell.mitarbeiter_id,
        datum: cell.datum,
        dienst_art: cell.dienst_art,
        status_code: statusCode,
      })
    );
  };

  const applyFunktion = () => {
    if (!selectedFahrzeugId || !selectedFunktionKuerzel) {
      toast.error('Bitte Fahrzeug und Funktion wählen');
      return;
    }
    runBulk((cell) =>
      dienstApi.setFunktionMitSpringer({
        mitarbeiter_id: cell.mitarbeiter_id,
        datum: cell.datum,
        dienst_art: cell.dienst_art,
        fahrzeug_id: selectedFahrzeugId,
        funktion_kuerzel: selectedFunktionKuerzel,
        springer_funktionen: [],
        ist_praktikant: !!mitarbeiter?.ist_praktikant,
        abteilung_id: abteilungId,
      })
    );
  };

  const clearAll = () => {
    runBulk((cell) =>
      dienstApi.clearEinteilung({
        mitarbeiter_id: cell.mitarbeiter_id,
        datum: cell.datum,
        dienst_art: cell.dienst_art,
      })
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col" data-testid="bulk-assign-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CalendarDays className="w-5 h-5" />
            Mehrfach-Zuteilung — {mitarbeiter?.nachname}, {mitarbeiter?.vorname}
          </DialogTitle>
          <DialogDescription>
            {cellCount} {cellCount === 1 ? 'Zelle' : 'Zellen'} markiert. Die Aktion wird auf alle angewendet.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-wrap gap-1 max-h-24 overflow-y-auto pb-2 border-b mb-3">
          {selectedCells.map((c, i) => (
            <Badge
              key={`${c.datum}-${c.dienst_art}-${i}`}
              variant="outline"
              className="text-[10px] font-mono"
              data-testid={`bulk-cell-${c.datum}-${c.dienst_art}`}
            >
              {c.datum} {c.dienst_art}
            </Badge>
          ))}
        </div>

        <Tabs value={mode} onValueChange={setMode} className="flex-1 flex flex-col overflow-hidden">
          <TabsList className="grid grid-cols-3 shrink-0">
            <TabsTrigger value="funktion" data-testid="bulk-tab-funktion">
              <Truck className="w-4 h-4 mr-1" /> Funktion
            </TabsTrigger>
            <TabsTrigger value="status" data-testid="bulk-tab-status">
              <AlertCircle className="w-4 h-4 mr-1" /> Abwesend
            </TabsTrigger>
            <TabsTrigger value="clear" data-testid="bulk-tab-clear">
              <Trash2 className="w-4 h-4 mr-1" /> Leeren
            </TabsTrigger>
          </TabsList>

          {/* FUNKTION */}
          <TabsContent value="funktion" className="flex-1 overflow-y-auto space-y-3 pt-3">
            <div className="space-y-2">
              <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                Fahrzeug
              </label>
              <div className="grid grid-cols-2 gap-2">
                {hauptFahrzeuge.map((fz) => (
                  <Button
                    key={fz.id}
                    variant={selectedFahrzeugId === fz.id ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => { setSelectedFahrzeugId(fz.id); setSelectedFunktionKuerzel(''); }}
                    className="justify-start"
                    data-testid={`bulk-fahrzeug-${fz.id}`}
                  >
                    <Truck className="w-4 h-4 mr-2" />
                    {fz.funkrufname}
                  </Button>
                ))}
              </div>
            </div>

            {selectedFahrzeugId && (
              <div className="space-y-2">
                <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                  Funktion
                </label>
                <div className="grid grid-cols-3 gap-2">
                  {funktionenFuerFahrzeug.map((fn) => {
                    const fz = hauptFahrzeuge.find((f) => f.id === selectedFahrzeugId);
                    const kuerzelHatZiffer = /\d$/.test(fn.kuerzel || '');
                    const fzNummer = extractFahrzeugNummer(fz?.funkrufname || '');
                    const display = (fzNummer && !kuerzelHatZiffer) ? `${fn.kuerzel}${fzNummer}` : fn.kuerzel;
                    return (
                      <Button
                        key={fn.kuerzel}
                        variant={selectedFunktionKuerzel === fn.kuerzel ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => setSelectedFunktionKuerzel(fn.kuerzel)}
                        data-testid={`bulk-funktion-${fn.kuerzel}`}
                      >
                        {display}
                      </Button>
                    );
                  })}
                </div>
              </div>
            )}

            <div className="pt-2">
              <Button
                onClick={applyFunktion}
                disabled={!selectedFahrzeugId || !selectedFunktionKuerzel || isSaving}
                className="w-full"
                data-testid="bulk-apply-funktion"
              >
                {isSaving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                Auf {cellCount} {cellCount === 1 ? 'Zelle' : 'Zellen'} anwenden
              </Button>
            </div>
          </TabsContent>

          {/* STATUS */}
          <TabsContent value="status" className="flex-1 overflow-y-auto space-y-3 pt-3">
            <p className="text-xs text-muted-foreground">
              Klick auf einen Status wendet ihn sofort auf alle {cellCount} markierten Zellen an.
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {STATUS_BUTTONS.map((code) => {
                const meta = STATUS_CODES[code];
                if (!meta) return null;
                return (
                  <Button
                    key={code}
                    variant="outline"
                    disabled={isSaving}
                    className={cn('h-14 flex-col gap-0.5', meta.bgLight, meta.textColor, meta.border, 'border-2')}
                    onClick={() => applyStatus(code)}
                    data-testid={`bulk-status-${code}`}
                  >
                    <span className="text-lg font-bold">{meta.displayCode}</span>
                    <span className="text-[10px] font-normal">{meta.label}</span>
                  </Button>
                );
              })}
            </div>
          </TabsContent>

          {/* CLEAR */}
          <TabsContent value="clear" className="flex-1 overflow-y-auto space-y-3 pt-3">
            <div className="p-4 border border-destructive/40 bg-destructive/5 rounded-md">
              <p className="text-sm font-semibold text-destructive mb-1">Alle markierten Zellen leeren</p>
              <p className="text-xs text-muted-foreground mb-3">
                Entfernt Funktion, Springer, Status und Bemerkung aus jeder markierten Zelle. Diese Aktion lässt sich
                nicht rückgängig machen.
              </p>
              <Button
                variant="destructive"
                onClick={clearAll}
                disabled={isSaving}
                data-testid="bulk-apply-clear"
              >
                {isSaving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
                {cellCount} {cellCount === 1 ? 'Zelle' : 'Zellen'} leeren
              </Button>
            </div>
          </TabsContent>
        </Tabs>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={isSaving} data-testid="bulk-cancel">
            Abbrechen
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default BulkAssignmentDialog;
